import React, { Suspense, useState, useEffect } from 'react';
import { Admin, Resource, CustomRoutes, localStorageStore } from 'react-admin';
import { Route, Navigate } from 'react-router-dom';
import { QueryClient } from '@tanstack/react-query';
import { StyledEngineProvider } from '@mui/material/styles';
import { CircularProgress, Box } from '@mui/material';

// Providers
import authProvider, { authEvents, AUTH_EVENTS } from './providers/authProvider';
import dataProvider from './providers/dataProvider';
import i18nProvider from './providers/i18nProvider';
import { PreferencesProvider } from './providers/preferencesProvider';

import { CustomLayout } from './layouts';
import { StartPage } from './layouts/StartPage';

import NotAuthorized from './components/common/NotAuthorized';

import { lightTheme } from './lightTheme';

import { getResourcesForReactAdmin } from './config/resourceConfig';
import { V2_PROPERTIES_QUERY_KEY } from './hooks/useV2PropertySelection';
import { DISPENSER_PRESENCE_QUERY_KEY } from './hooks/useHasDispensers';

import { KioskSimulator } from './features/simulators/kiosk/components/KioskSimulator';
import { SimulatorsIndex } from './features/simulators/shared/components/SimulatorsIndex';
import { DispenserSimulator } from './features/simulators/dispenser/components/DispenserSimulator';
import { MobileDeskSimulator } from './features/simulators/mobile-desk/components/MobileDeskSimulator';
import { MobileKioskSimulator } from './features/simulators/mobile-kiosk/components/MobileKioskSimulator';
import { OnlineCheckInSimulator } from './features/simulators/online-checkin/components/OnlineCheckInSimulator';
import { ReservationDetailPage } from './features/reservations';
import { ArrivalsWallScreen } from './panel/ArrivalsWallScreen';
import { ManualPage } from './features/manual';
import { AssistantConnectPage } from './features/assistant-connect';
import { ReleaseNotesPage } from './features/release-notes';
import { DevelopersPage, KeysPage } from './features/developers';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Persistent app store. Seed the theme preference to 'light' so a stale
// 'dark' value from before the theme went light-only cannot matter.
const store = localStorageStore();
if (store.getItem('theme', null) === null) {
  store.setItem('theme', 'light');
}

const App: React.FC = () => {
  const [resources, setResources] = useState(() => getResourcesForReactAdmin());
  const [adminKey, setAdminKey] = useState(0);

  useEffect(() => {
    const reloadResources = () => {
      // The v2 property list and the dispenser presence belong to the user
      // who fetched them; the next user must not inherit them (they decide
      // which screens the rail offers).
      queryClient.removeQueries({ queryKey: V2_PROPERTIES_QUERY_KEY });
      queryClient.removeQueries({ queryKey: DISPENSER_PRESENCE_QUERY_KEY });
      const newResources = getResourcesForReactAdmin();
      setResources(newResources);
      setAdminKey(prev => prev + 1);
    };

    const handleLoginSuccess = () => {
      setTimeout(() => {
        reloadResources();
      }, 100);
    };

    const handleLogout = () => {
      reloadResources();
    };

    const handleRoleChanged = () => {
      reloadResources();
    };

    // Subscribe to auth events
    authEvents.addEventListener(AUTH_EVENTS.LOGIN_SUCCESS, handleLoginSuccess);
    authEvents.addEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
    authEvents.addEventListener(AUTH_EVENTS.ROLE_CHANGED, handleRoleChanged);

    // Cleanup
    return () => {
      authEvents.removeEventListener(AUTH_EVENTS.LOGIN_SUCCESS, handleLoginSuccess);
      authEvents.removeEventListener(AUTH_EVENTS.LOGOUT, handleLogout);
      authEvents.removeEventListener(AUTH_EVENTS.ROLE_CHANGED, handleRoleChanged);
    };
  }, []);

  return (
    <Suspense
      fallback={
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      }
    >
      <StyledEngineProvider injectFirst>
        <PreferencesProvider>
        <Admin
          key={adminKey}
          authProvider={authProvider}
          dataProvider={dataProvider}
          i18nProvider={i18nProvider}
          queryClient={queryClient}
          store={store}
          theme={lightTheme}
          layout={CustomLayout}
          requireAuth
          disableTelemetry
        >

          {resources.map(resource => (
            <Resource
              key={resource.name}
              {...resource}
            />
          ))}


          <CustomRoutes>
            <Route path="/not-authorized" element={<NotAuthorized />} />
            <Route path="/simulators/kiosk" element={<KioskSimulator />} />
            <Route path="/simulators" element={<SimulatorsIndex />} />
            <Route path="/simulators/dispenser" element={<DispenserSimulator />} />
            <Route path="/simulators/mobile-desk" element={<MobileDeskSimulator />} />
            <Route path="/simulators/mobile-kiosk" element={<MobileKioskSimulator />} />
            <Route path="/simulators/online-checkin" element={<OnlineCheckInSimulator />} />
            {/* Reservation detail (mockup 04) — deliberately NOT the resource's
                default /:id/show route, so the address stays /reservations/:id. */}
            <Route path="/reservations/:id" element={<ReservationDetailPage />} />
            {/* AI-assistant connection: the page guards itself with the same role rule as the app-bar entry. */}
            <Route path="/assistant" element={<AssistantConnectPage />} />
            {/* User manual: every signed-in role; the API cuts the edition per role. */}
            <Route path="/manual" element={<ManualPage />} />
            <Route path="/manual/:chapterId" element={<ManualPage />} />
            {/* Release notes history: its own route, not under /manual, so rail and breadcrumb never match both. */}
            <Route path="/novedades" element={<ReleaseNotesPage />} />
            {/* Developers: the public API documentation (every role but Receptionist, and documentation-only
                people); `/developers/keys` is the staff key management, outside the four tabs. */}
            <Route path="/developers" element={<DevelopersPage />} />
            <Route path="/developers/keys" element={<KeysPage />} />

            {/* Retired pages (Decision 11, wave 3; F-B-025 for Operations):
                redirect old bookmarks/links instead of a broken route.
                Actividad → Llegadas de hoy, Check-in list and Recorridos →
                Reservas, Operaciones → Eventos. "/" (the old Dashboard) is
                the user's start page, one they are allowed to see. */}
            <Route path="/" element={<StartPage />} />
            <Route path="/activity-monitor" element={<Navigate to="/arrivals" replace />} />
            <Route path="/check-ins" element={<Navigate to="/reservations" replace />} />
            <Route path="/journeys" element={<Navigate to="/reservations" replace />} />
            <Route path="/operations" element={<Navigate to="/events" replace />} />
          </CustomRoutes>

          {/* Pantalla de pared (mockup 02) — authenticated, no rail/app bar. Distinct
              from the public token panel at #/panel/<token> (src/panel/PanelApp.tsx),
              which short-circuits before this Admin tree even mounts. */}
          <CustomRoutes noLayout>
            <Route path="/panel" element={<ArrivalsWallScreen />} />
          </CustomRoutes>

        </Admin>
        </PreferencesProvider>
      </StyledEngineProvider>
    </Suspense>
  );
};

export default App;
